import usePaymentFlow from '../../../hooks/payment/usePaymentFlow';
import { usePaymentCoordinator } from '../../../hooks/payment/usePaymentCoordinator';
import { OnlineShopWebViewPaymentResponseInterface } from '../../../services/exports/Interfaces';
import { PaymentIntent } from '@stripe/stripe-js';
import {
  OnlineShopWebViewMessageTypes,
  PaymentStatus,
  isSpreedlyOffsitePayment,
} from '../../../services/exports/Constants';
import CustomButton from '../../../components-library/buttons/CustomButton';
import type { CheckoutFunction } from '../../../hooks/payment/useOrderCheckout';
import { useEffect } from 'react';
import useGiftCardCheckout, {
  useGenerateGiftCardReturnUrl,
  useHandleGiftCardCheckoutResult,
} from '../../../hooks/payment/useGiftCardCheckout';
import { useTranslation } from 'react-i18next';
import PaymentInitializer from '../../../components-library/payment/PaymentInitializer';
import { ChildPayButtonProps, PayButtonProps } from '../../../components-library/payment/buttons/types';
import AdyenPayButton from '../../../components-library/payment/buttons/AdyenPayButton';
import StripePayButton from '../../../components-library/payment/buttons/StripePayButton';
import SpreedlyPayButton from '../../../components-library/payment/buttons/SpreedlyPayButton';
import SpreedlyOffsitePayButton from '../../../components-library/payment/buttons/SpreedlyOffsitePayButton';
import { ConfirmPaymentFunction } from '../../../hooks/payment/useCheckout';
import { useStripeElementsSession } from '../../../components-library/payment/StripeElementsMounted';
import type { CheckoutOptions } from '../../../hooks/payment/useOrderCheckout';
import { ButtonTypes, OrderData } from '../../../services/exports/Interfaces';

/**
 * Rendered when the selected method routes to a gateway without a button of its own. Disabled
 * on purpose: the previous `?? null` made React render a null component type, which crashed the
 * whole gift-card checkout for any company on an unmapped gateway.
 */
function DefaultButton({ title, className }: ChildPayButtonProps) {
  return (
    <CustomButton
      testId="pay-button"
      buttonType={ButtonTypes.primary}
      title={title}
      disabled
      className={className}
      showArrow
    />
  );
}

function PayButtonContent({ title, className }: PayButtonProps) {
  const { selectedPaymentMethod, getGatewayForMethod } = usePaymentFlow();
  const { generateReturnUrl } = useGenerateGiftCardReturnUrl();

  const {
    loading: paymentsLoading,
    isPaymentProcessing: loading,
    setIsPaymentProcessing: setLoading,
  } = usePaymentCoordinator();
  // As on the order checkout: behind the Stripe Elements block's placeholder, the selection is not on
  // screen yet. Always false outside that checkout.
  const { blockPending: paymentBlockPending } = useStripeElementsSession();

  const { onPaymentError, onPaymentSuccess } = useHandleGiftCardCheckoutResult();
  const { checkout, loading: _loading } = useGiftCardCheckout({
    loading,
    setLoading,
  });

  useEffect(() => {
    document.addEventListener('message', resolveWebViewMessage);

    return () => document.removeEventListener('message', resolveWebViewMessage);
  }, []);

  const resolveWebViewMessage = (message: any): void => {
    if (message?.data?.type === OnlineShopWebViewMessageTypes.PaymentResult) {
      handlePaymentResultFromWebView(message.data.payload);
    }
  };

  const handlePaymentResultFromWebView = (payload: OnlineShopWebViewPaymentResponseInterface): boolean => {
    const { payment_result, order } = payload;

    if (payment_result.status === PaymentStatus.Succeeded) {
      return onPaymentSuccess(order);
    }

    return onPaymentError();
  };

  async function onClick(
    confirmPayment: ConfirmPaymentFunction,
    paymentIntent?: PaymentIntent,
    customCheckout?: CheckoutFunction,
    options?: CheckoutOptions,
    extraData?: Partial<OrderData>,
  ): Promise<boolean> {
    const checkoutFunction = customCheckout ?? checkout;

    return await checkoutFunction(confirmPayment, paymentIntent, extraData, undefined, options);
  }

  const childProps = {
    title,
    loading: _loading,
    disabled: !selectedPaymentMethod,
    silentlyDisabled: paymentsLoading || paymentBlockPending,
    setLoading,
    onClick,
    onPaymentSuccess,
    onPaymentError,
    generateReturnUrl,
    // The GIFT-CARD checkout: SpreedlyPayButton wraps whatever checkout it is handed.
    checkout,
    className,
  };

  const routedGateway = getGatewayForMethod(selectedPaymentMethod);
  const PaymentButtonComponent =
    {
      adyen: AdyenPayButton,
      stripe: StripePayButton,
      // As on the order checkout: an offsite method goes to the bank or wallet, not the card form.
      spreedly: isSpreedlyOffsitePayment(selectedPaymentMethod) ? SpreedlyOffsitePayButton : SpreedlyPayButton,
    }[routedGateway] ?? DefaultButton;

  return (
    <>
      <PaymentButtonComponent {...childProps} />
    </>
  );
}

export default function PayButton(props: PayButtonProps) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Payment:Buttons:PayButton' });

  const { isOnlinePayment, setIsGiftCardCheckout } = usePaymentCoordinator();

  useEffect(() => {
    setIsGiftCardCheckout(true);
  }, []);

  if (!isOnlinePayment) {
    return null;
  }

  return (
    <PaymentInitializer fallback={<CustomButton title={t('secure_checkout')} loading />}>
      <PayButtonContent {...props} />
    </PaymentInitializer>
  );
}
